/**
 * NewLocationRow
 */

import { useContext, useState } from 'react';
import { LocalizationContext } from 'context/localization.context';
import Tooltip from 'components/ui/Tooltip';
import { ContactModel, LocationRowModel } from 'types/common';
import RichText from 'components/ui/RichText';
import Icon from 'components/ui/Icon';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import clsx from 'clsx';
import { PhoneIcon } from '../LocationRow/LocationRow.styles';

const renderContacts = (items: ContactModel[]) => {
	if (!items || items?.length < 0) return;
	return items.map((contact: any, i: number) => {
		const { name, role, image, productArea, phone, email } = contact;
		return (
			<li
				id="contact-list"
				className={clsx(
					'w-full flex flex-col lg:flex-row mt-10 border-greyDark pb-5',
					i === items.length - 1 ? 'border-0' : 'border-b'
				)}
				key={i.toString()}
			>
				<div className="flex flex-1 mb-3">
					{image && (
						<img
							src={image.src}
							alt={image.alt}
							className="new-location-row-contact-img"
						/>
					)}
					<div className="flex flex-col">
						<h4 className=" text-h4 text-blue font-bold font-standard">
							{name}
						</h4>
						<span className="text-p leading-9 my-2 font-normal">{role}</span>
						<span className="text-p leading-4 my-2 font-normal">
							{productArea}
						</span>
					</div>
				</div>
				<div className="flex flex-1 flex-col new-location-row-contact-phone-email">
					<span className="flex gap-2 items-center ">
						<PhoneIcon aria-hidden className="mr-0" /> {phone}
					</span>
					<span className="flex gap-2 items-center">
						<Icon icon="mail" /> {email}
					</span>
				</div>
			</li>
		);
	});
};

/** Main description for this component. */
const NewLocationRow = ({
	title,
	categories,
	city,
	contacts,
	highlightedContacts,
	email,
	emailTooltip,
	email2,
	email2Tooltip,
	mapLinkUrl,
	openingHours,
	phone,
	phoneTooltip,
	phone2,
	phone2Tooltip,
	phone3,
	phone3Tooltip,
	streetAddress,
	website,
	websiteTitle,
	websiteTooltip,
	query,
	milkRun,
	hitId,
	...restProps
}: LocationRowModel) => {
	const { t }: any = useContext(LocalizationContext);
	const [nestedOpen, setNestedOpen] = useState(false);

	return (
		<article className="border border-greyDarkest h-auto w-full rounded-2xl p-8 mb-6 xl:w-768px">
			{title && <h3 className="text-h2 text-blue mb-5 font-bold">{title}</h3>}
			<div className="flex flex-col lg:flex-row gap-6 mb-7">
				<div className="w-full lg:w-grid-4/12 mb-5 lg:mb-0">
					<div className="border-b border-greyDark pb-2.5 flex items-center mb-4">
						<Icon icon="mapPin" size={1} className="mr-2" />

						<h3 className="text-h4 font-semibold font-standard">{city}</h3>
					</div>
					<address className="not-italic" id="location-address">
						<RichText text={streetAddress} />
					</address>
					<a
						className="text-p flex items-center gap-2 text-blue mt-2"
						href={mapLinkUrl}
					>
						<span className="underline">{t('contactpage/openmap')}</span>
						<Icon icon="externalLink" size={1} className="mr-2" />
					</a>
				</div>
				<div className="w-full lg:w-grid-4/12 mb-5 lg:mb-0">
					<div className="border-b border-greyDark pb-2.5 flex items-center mb-4">
						<Icon icon="clock" size={1} className="mr-2" />

						<h3 className="text-h4 font-semibold font-standard">
							{t('contactpage/oppetider')}
						</h3>
					</div>
					<RichText text={openingHours} />
				</div>
				<div className="w-full lg:w-grid-4/12 mb-5 lg:mb-0 lg:flex-grow">
					<div className="border-b border-greyDark pb-2.5 flex items-center mb-4">
						<h3 className="text-h4 font-semibold font-standard flex items-center">
							<PhoneIcon
								aria-hidden
								//@ts-ignore
							/>

							{t('contactpage/kontakt')}
						</h3>
					</div>

					<div className="flex flex-col gap-1">
						{phone && (
							<span className="flex items-center">
								<a
									href={`tel:${phone}`}
									className="text-p flex gap-2 items-center"
								>
									{phone}
								</a>
								{phoneTooltip && <Tooltip hover={true}>{phoneTooltip}</Tooltip>}
							</span>
						)}
						{email && (
							<span className="flex items-center">
								<a
									href={`mailto:${email}`}
									className="text-p flex gap-2 items-center"
								>
									<Icon icon="mail" aria-hidden /> {email}
								</a>
								{emailTooltip && <Tooltip hover={true}>{emailTooltip}</Tooltip>}
							</span>
						)}
						{website && websiteTitle && (
							<span className="flex items-center">
								<a href={website} className="text-p flex gap-2 items-center">
									<Icon icon="externalLink" aria-hidden />

									{websiteTitle}
								</a>
								{websiteTooltip && (
									<Tooltip hover={true}>{websiteTooltip}</Tooltip>
								)}
							</span>
						)}
					</div>
				</div>
			</div>
			{highlightedContacts && (
				<details
					className="h-auto min-h-[40px] py-3 px-4 rounded-2xl w-full bg-white mb-2.5"
					open
				>
					<summary className="cursor-pointer list-none new-location-row-heading flex justify-between">
						{t('contactpage/showfilteredsellersandexperts')}
						{<FiChevronDown size={25} />}
					</summary>
					<ul className="">{renderContacts(highlightedContacts)}</ul>
					{contacts && (
						<details className="mb-2.5">
							<summary className="cursor-pointer list-none new-location-row-heading flex justify-between text-p font-semibold">
								{t('contactpage/showunfilteredsellersandexperts')}
								{nestedOpen ? (
									<FiChevronDown
										onClick={() => setNestedOpen(!nestedOpen)}
										size={25}
									/>
								) : (
									<FiChevronUp
										onClick={() => setNestedOpen(!nestedOpen)}
										size={25}
									/>
								)}
							</summary>
							<ul>{renderContacts(contacts)}</ul>
						</details>
					)}
				</details>
			)}

			{contacts && !highlightedContacts && (
				<details className="h-auto min-h-[40px] py-3 px-4 rounded-2xl w-full bg-white mb-2.5">
					<summary className="cursor-pointer list-none new-location-row-heading flex justify-between text-p font-semibold">
						{t('contactpage/showsellersandexperts')}
						{<FiChevronDown size={25} />}
					</summary>
					<ul>{renderContacts(contacts)}</ul>
				</details>
			)}

			{milkRun && (
				<details className="h-auto min-h-[40px] py-3 px-4 rounded-2xl w-full bg-white">
					<summary className="cursor-pointer list-none new-location-row-heading flex justify-between text-p font-semibold">
						{t('contactpage/showmilkrun')}
						{milkRun && <FiChevronDown size={25} />}
					</summary>

					<RichText text={milkRun} className="p-5" />
				</details>
			)}
		</article>
	);
};

export default NewLocationRow;
